/* ═══════════════════════════════════════════════════════
   STUNTING.ID — WebGL scenes (Three.js)
   1. Hero      : heliks DNA + partikel gizi
   2. Growth    : perbandingan tinggi badan 3D
   3. Map       : diagram batang prevalensi 3D
   4. Nutrients : model molekul bola-batang
   ═══════════════════════════════════════════════════════ */

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

const D = window.SITE_DATA;
const PR = () => Math.min(window.devicePixelRatio || 1, 2);
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ── deteksi WebGL ───────────────────────────────────── */
function hasWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch (e) { return false; }
}

/* ── kerangka umum sebuah stage ──────────────────────── */
function createStage(canvas, opts = {}) {
  const host = canvas.parentElement;
  const renderer = new THREE.WebGLRenderer({
    canvas, antialias: true, alpha: true, powerPreference: 'high-performance'
  });
  renderer.setPixelRatio(PR());
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = opts.exposure ?? 1.05;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(opts.fov ?? 45, 1, 0.1, 400);
  camera.position.set(...(opts.camera ?? [0, 0, 12]));

  const stage = {
    renderer, scene, camera, host, canvas,
    clock: new THREE.Clock(),
    controls: null,
    visible: false,
    running: false,
    onFrame: null,
    onResize: null
  };

  function resize() {
    const w = host.clientWidth || 1;
    const h = host.clientHeight || 1;
    renderer.setPixelRatio(PR());
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    if (stage.onResize) stage.onResize(w, h);
  }
  stage.resize = resize;
  resize();
  new ResizeObserver(resize).observe(host);

  function loop() {
    if (!stage.running) return;
    requestAnimationFrame(loop);
    const dt = Math.min(stage.clock.getDelta(), 0.05);
    if (stage.controls) stage.controls.update();
    if (stage.onFrame) stage.onFrame(dt, stage.clock.elapsedTime);
    renderer.render(scene, camera);
  }
  stage.start = () => { if (!stage.running) { stage.running = true; stage.clock.getDelta(); loop(); } };
  stage.stop = () => { stage.running = false; };

  // render hanya saat terlihat di layar
  new IntersectionObserver((entries) => {
    entries.forEach(e => { e.isIntersecting ? stage.start() : stage.stop(); });
  }, { threshold: 0.01 }).observe(host);

  return stage;
}

/** OrbitControls yang tidak membajak scroll halaman di perangkat sentuh. */
function attachControls(stage, cfg = {}) {
  const c = new OrbitControls(stage.camera, stage.renderer.domElement);
  c.enableDamping = true;
  c.dampingFactor = 0.06;
  c.enablePan = false;
  c.enableZoom = !!cfg.zoom;
  c.minDistance = cfg.min ?? 4;
  c.maxDistance = cfg.max ?? 60;
  c.minPolarAngle = cfg.minPolar ?? 0.25;
  c.maxPolarAngle = cfg.maxPolar ?? Math.PI / 2 + 0.15;
  c.autoRotate = !!cfg.autoRotate && !reduceMotion;
  c.autoRotateSpeed = cfg.autoRotateSpeed ?? 0.6;
  // satu jari = scroll halaman, dua jari = putar/zoom
  c.touches = { ONE: null, TWO: cfg.zoom ? THREE.TOUCH.DOLLY_ROTATE : THREE.TOUCH.ROTATE };
  if (cfg.target) c.target.set(...cfg.target);
  c.update();
  stage.controls = c;

  // hentikan auto-rotate saat pengguna berinteraksi
  if (c.autoRotate) {
    let t;
    const pause = () => { c.autoRotate = false; clearTimeout(t); t = setTimeout(() => { c.autoRotate = true; }, 4000); };
    stage.renderer.domElement.addEventListener('pointerdown', pause);
    stage.renderer.domElement.addEventListener('wheel', pause, { passive: true });
  }
  return c;
}

/** Label bertekstur canvas → Sprite. */
function makeLabel(text, { color = '#e8eef7', size = 46, bg = 'rgba(9,14,24,.82)', bold = 600, pad = 18 } = {}) {
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d');
  const font = `${bold} ${size}px Outfit, system-ui, sans-serif`;
  ctx.font = font;
  const w = Math.ceil(ctx.measureText(text).width) + pad * 2;
  const h = size + pad * 1.4;
  c.width = w * 2; c.height = h * 2;
  ctx.scale(2, 2);
  ctx.font = font;
  ctx.fillStyle = bg;
  const r = h / 2;
  ctx.beginPath();
  ctx.moveTo(r, 0); ctx.lineTo(w - r, 0); ctx.quadraticCurveTo(w, 0, w, r);
  ctx.lineTo(w, h - r); ctx.quadraticCurveTo(w, h, w - r, h);
  ctx.lineTo(r, h); ctx.quadraticCurveTo(0, h, 0, h - r);
  ctx.lineTo(0, r); ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.fill();
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, w / 2, h / 2 + 1);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
  spr.renderOrder = 10;
  spr.scale.set((w / h) * 0.55, 0.55, 1);
  spr.userData.aspect = w / h;
  return spr;
}

function setLabel(sprite, text, opts) {
  const fresh = makeLabel(text, opts);
  sprite.material.map.dispose();
  sprite.material.map = fresh.material.map;
  sprite.material.needsUpdate = true;
  sprite.scale.set(fresh.scale.x, fresh.scale.y, 1);
  fresh.material.dispose();
}

/* ═══════════════════════════════════════════════════════
   1. HERO — heliks DNA & partikel
   ═══════════════════════════════════════════════════════ */
function initHero() {
  const canvas = document.getElementById('heroCanvas');
  if (!canvas) return;
  const stage = createStage(canvas, { camera: [0, 0, 15], fov: 42, exposure: 1.15 });
  const { scene, camera } = stage;

  scene.add(new THREE.AmbientLight(0x8fb0ff, 0.55));
  const key = new THREE.PointLight(0x2ee6c5, 260, 90); key.position.set(9, 8, 10); scene.add(key);
  const rim = new THREE.PointLight(0x8b6cff, 190, 90); rim.position.set(-10, -5, 6); scene.add(rim);
  const fill = new THREE.DirectionalLight(0x4a8cff, 0.8); fill.position.set(-4, 6, 8); scene.add(fill);

  const world = new THREE.Group();
  world.position.set(4.6, 0, 0);
  world.rotation.z = 0.18;
  scene.add(world);

  /* ── heliks ganda ── */
  const helix = new THREE.Group();
  world.add(helix);

  const TURNS = 4.2, STEPS = 132, RAD = 2.5, H = 24;
  const sphereGeo = new THREE.IcosahedronGeometry(0.17, 2);
  const matA = new THREE.MeshStandardMaterial({ color: 0x2ee6c5, emissive: 0x0d6f5e, roughness: 0.28, metalness: 0.35 });
  const matB = new THREE.MeshStandardMaterial({ color: 0x7fa8ff, emissive: 0x1c3b7a, roughness: 0.28, metalness: 0.35 });

  const strandA = new THREE.InstancedMesh(sphereGeo, matA, STEPS);
  const strandB = new THREE.InstancedMesh(sphereGeo, matB, STEPS);
  helix.add(strandA, strandB);

  const rungGeo = new THREE.CylinderGeometry(0.035, 0.035, 1, 6);
  const rungMat = new THREE.MeshStandardMaterial({
    color: 0xffffff, emissive: 0x2ee6c5, emissiveIntensity: 0.35,
    transparent: true, opacity: 0.34, roughness: 0.5
  });
  const RUNGS = Math.floor(STEPS / 3);
  const rungs = new THREE.InstancedMesh(rungGeo, rungMat, RUNGS);
  helix.add(rungs);

  const dummy = new THREE.Object3D();
  const pA = new THREE.Vector3(), pB = new THREE.Vector3();

  function layout(t) {
    for (let i = 0; i < STEPS; i++) {
      const f = i / (STEPS - 1);
      const a = f * Math.PI * 2 * TURNS + t * 0.35;
      const y = (f - 0.5) * H;
      const breathe = 1 + Math.sin(f * Math.PI * 3 + t * 0.9) * 0.05;
      const r = RAD * breathe;

      pA.set(Math.cos(a) * r, y, Math.sin(a) * r);
      pB.set(Math.cos(a + Math.PI) * r, y, Math.sin(a + Math.PI) * r);

      const s = 0.7 + Math.sin(f * Math.PI) * 0.75;
      dummy.position.copy(pA); dummy.scale.setScalar(s); dummy.updateMatrix();
      strandA.setMatrixAt(i, dummy.matrix);
      dummy.position.copy(pB); dummy.updateMatrix();
      strandB.setMatrixAt(i, dummy.matrix);

      if (i % 3 === 0) {
        const ri = i / 3;
        if (ri < RUNGS) {
          dummy.position.copy(pA).lerp(pB, 0.5);
          dummy.scale.set(1, pA.distanceTo(pB), 1);
          dummy.quaternion.setFromUnitVectors(
            new THREE.Vector3(0, 1, 0),
            pB.clone().sub(pA).normalize()
          );
          dummy.updateMatrix();
          rungs.setMatrixAt(ri, dummy.matrix);
        }
      }
    }
    strandA.instanceMatrix.needsUpdate = true;
    strandB.instanceMatrix.needsUpdate = true;
    rungs.instanceMatrix.needsUpdate = true;
  }
  layout(0);

  /* ── partikel gizi mengambang ── */
  const N = 620;
  const pos = new Float32Array(N * 3);
  const col = new Float32Array(N * 3);
  const seed = new Float32Array(N);
  const palette = [new THREE.Color(0x2ee6c5), new THREE.Color(0x4a8cff), new THREE.Color(0x8b6cff), new THREE.Color(0xffb648)];
  for (let i = 0; i < N; i++) {
    const r = 6 + Math.random() * 16;
    const th = Math.random() * Math.PI * 2;
    const ph = Math.acos(2 * Math.random() - 1);
    pos[i * 3] = Math.sin(ph) * Math.cos(th) * r;
    pos[i * 3 + 1] = (Math.random() - 0.5) * 34;
    pos[i * 3 + 2] = Math.sin(ph) * Math.sin(th) * r * 0.6;
    const c = palette[(Math.random() * palette.length) | 0];
    col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
    seed[i] = Math.random() * 100;
  }
  const pGeo = new THREE.BufferGeometry();
  pGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  pGeo.setAttribute('color', new THREE.BufferAttribute(col, 3));

  // sprite lingkaran lembut
  const dotCanvas = document.createElement('canvas');
  dotCanvas.width = dotCanvas.height = 64;
  const dctx = dotCanvas.getContext('2d');
  const grd = dctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.35, 'rgba(255,255,255,.65)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  dctx.fillStyle = grd; dctx.fillRect(0, 0, 64, 64);
  const dotTex = new THREE.CanvasTexture(dotCanvas);

  const points = new THREE.Points(pGeo, new THREE.PointsMaterial({
    size: 0.30, map: dotTex, vertexColors: true, transparent: true,
    opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true
  }));
  world.add(points);

  /* ── cincin orbit dekoratif ── */
  const ringMat = new THREE.MeshBasicMaterial({ color: 0x2ee6c5, transparent: true, opacity: 0.12, side: THREE.DoubleSide });
  const rings = [];
  [6.5, 8.6, 10.8].forEach((r, i) => {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.012, 8, 160), ringMat.clone());
    ring.rotation.x = Math.PI / 2 + (i - 1) * 0.22;
    ring.material.opacity = 0.14 - i * 0.03;
    world.add(ring); rings.push(ring);
  });

  /* ── parallax kursor ── */
  const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
  window.addEventListener('pointermove', (e) => {
    mouse.tx = (e.clientX / window.innerWidth - 0.5) * 2;
    mouse.ty = (e.clientY / window.innerHeight - 0.5) * 2;
  }, { passive: true });

  let scrollY = 0;
  window.addEventListener('scroll', () => { scrollY = window.scrollY; }, { passive: true });

  const basePos = new THREE.Vector3(4.6, 0, 0);

  stage.onResize = (w) => {
    // pada layar sempit, geser heliks ke tengah & mundurkan kamera
    const narrow = w < 900;
    basePos.set(narrow ? 0 : 4.6, narrow ? -1.5 : 0, 0);
    camera.position.z = narrow ? 19 : 15;
  };
  stage.onResize(stage.host.clientWidth);

  stage.onFrame = (dt, t) => {
    mouse.x += (mouse.tx - mouse.x) * 0.045;
    mouse.y += (mouse.ty - mouse.y) * 0.045;

    if (!reduceMotion) layout(t);
    helix.rotation.y = t * 0.16;
    points.rotation.y = -t * 0.035;
    rings.forEach((r, i) => { r.rotation.z = t * (0.06 + i * 0.02) * (i % 2 ? -1 : 1); });

    world.position.x = basePos.x + mouse.x * 0.9;
    world.position.y = basePos.y - mouse.y * 0.6 + Math.sin(t * 0.5) * 0.25;
    world.rotation.x = -mouse.y * 0.12;
    world.rotation.y = mouse.x * 0.16;

    camera.position.y = -scrollY * 0.004;
    camera.lookAt(0, camera.position.y * 0.35, 0);
  };

  return stage;
}

/* ═══════════════════════════════════════════════════════
   2. GROWTH — perbandingan tinggi badan
   ═══════════════════════════════════════════════════════ */
function buildChild(color, accent) {
  // figur bergaya, tinggi total = 1 satuan (kaki di y=0, ujung kepala di y=1)
  const g = new THREE.Group();
  const skin = new THREE.MeshStandardMaterial({
    color, roughness: 0.42, metalness: 0.12,
    emissive: new THREE.Color(color).multiplyScalar(0.18)
  });
  const dark = new THREE.MeshStandardMaterial({ color: accent, roughness: 0.55, metalness: 0.1 });

  const add = (geo, mat, x, y, z, rz = 0) => {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, y, z); m.rotation.z = rz;
    g.add(m); return m;
  };

  // kaki
  const legGeo = new THREE.CapsuleGeometry(0.055, 0.30, 6, 14);
  add(legGeo, dark, -0.075, 0.21, 0);
  add(legGeo, dark, 0.075, 0.21, 0);
  // badan
  add(new THREE.CapsuleGeometry(0.125, 0.20, 8, 18), skin, 0, 0.52, 0);
  // lengan
  const armGeo = new THREE.CapsuleGeometry(0.042, 0.24, 6, 12);
  add(armGeo, skin, -0.175, 0.53, 0, 0.30);
  add(armGeo, skin, 0.175, 0.53, 0, -0.30);
  // leher + kepala
  add(new THREE.CylinderGeometry(0.045, 0.05, 0.05, 12), skin, 0, 0.70, 0);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.135, 28, 22), skin);
  head.position.set(0, 0.855, 0);
  head.scale.set(1, 1.06, 0.97);
  g.add(head);
  // rambut
  const hair = new THREE.Mesh(new THREE.SphereGeometry(0.142, 24, 18, 0, Math.PI * 2, 0, Math.PI * 0.58), dark);
  hair.position.copy(head.position); hair.position.y += 0.012;
  g.add(hair);

  g.userData.head = head;
  return g;
}

function initGrowth() {
  const canvas = document.getElementById('growthCanvas');
  if (!canvas) return;
  const stage = createStage(canvas, { camera: [0, 1.9, 6.4], fov: 42 });
  const { scene } = stage;

  scene.add(new THREE.AmbientLight(0xbcd0ff, 0.7));
  const d1 = new THREE.DirectionalLight(0xffffff, 1.35); d1.position.set(4, 8, 6); scene.add(d1);
  const d2 = new THREE.DirectionalLight(0x4a8cff, 0.55); d2.position.set(-6, 3, -4); scene.add(d2);
  const p1 = new THREE.PointLight(0x2ee6c5, 40, 22); p1.position.set(-2.4, 2.4, 3); scene.add(p1);
  const p2 = new THREE.PointLight(0xff8a3d, 40, 22); p2.position.set(2.4, 2.4, 3); scene.add(p2);

  const world = new THREE.Group();
  world.position.y = -1.25;
  scene.add(world);

  /* ── lantai + grid ── */
  const floor = new THREE.Mesh(
    new THREE.CircleGeometry(5.2, 64),
    new THREE.MeshStandardMaterial({ color: 0x0b1424, roughness: 0.9, metalness: 0.05 })
  );
  floor.rotation.x = -Math.PI / 2;
  world.add(floor);
  const grid = new THREE.PolarGridHelper(5.1, 12, 6, 72, 0x1d3350, 0x152640);
  grid.material.transparent = true; grid.material.opacity = 0.55;
  grid.position.y = 0.002;
  world.add(grid);

  /* ── penggaris ── */
  const SCALE = 0.028;           // 1 cm → 0.028 satuan dunia
  const RULER_MAX = 125;         // cm
  const ruler = new THREE.Group();
  ruler.position.set(0, 0, -0.55);
  world.add(ruler);

  const postMat = new THREE.MeshStandardMaterial({ color: 0x24406b, roughness: 0.6, emissive: 0x0a1830 });
  const post = new THREE.Mesh(new THREE.BoxGeometry(0.05, RULER_MAX * SCALE, 0.05), postMat);
  post.position.y = (RULER_MAX * SCALE) / 2;
  ruler.add(post);

  const tickMat = new THREE.MeshBasicMaterial({ color: 0x3d5f92, transparent: true, opacity: 0.75 });
  const tickBig = new THREE.MeshBasicMaterial({ color: 0x6f93c9 });
  for (let cm = 10; cm <= RULER_MAX; cm += 10) {
    const big = cm % 20 === 0;
    const t = new THREE.Mesh(new THREE.BoxGeometry(big ? 0.32 : 0.18, 0.012, 0.012), big ? tickBig : tickMat);
    t.position.set(big ? 0.19 : 0.12, cm * SCALE, 0);
    ruler.add(t);
    if (big) {
      const lb = makeLabel(`${cm}`, { size: 34, color: '#7f9dc9', bg: 'rgba(0,0,0,0)', pad: 6 });
      lb.scale.multiplyScalar(0.5);
      lb.position.set(0.62, cm * SCALE, 0);
      ruler.add(lb);
    }
  }

  /* ── dua figur ── */
  const okColor = 0x2ee6c5, badColor = 0xff8a3d;
  const normal = buildChild(okColor, 0x179e86);
  const stunt = buildChild(badColor, 0xc45c1f);
  normal.position.set(-1.15, 0, 0.35);
  stunt.position.set(1.15, 0, 0.35);
  world.add(normal, stunt);

  // bayangan lembut
  const shadowMat = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.32 });
  [[-1.15], [1.15]].forEach(([x]) => {
    const s = new THREE.Mesh(new THREE.CircleGeometry(0.42, 32), shadowMat);
    s.rotation.x = -Math.PI / 2; s.position.set(x, 0.004, 0.35);
    world.add(s);
  });

  // label tinggi di atas kepala
  const labNormal = makeLabel('87.1 cm', { size: 40, color: '#2ee6c5' });
  const labStunt = makeLabel('81.0 cm', { size: 40, color: '#ff8a3d' });
  [labNormal, labStunt].forEach(l => { l.scale.multiplyScalar(0.62); world.add(l); });

  // garis pembanding pada tinggi anak stunting
  const gapLine = new THREE.Mesh(
    new THREE.BoxGeometry(2.6, 0.014, 0.014),
    new THREE.MeshBasicMaterial({ color: 0xff5470, transparent: true, opacity: 0.65 })
  );
  gapLine.position.set(0, 1, 0.35);
  world.add(gapLine);

  const gapLabel = makeLabel('selisih 6.1 cm', { size: 34, color: '#ff5470' });
  gapLabel.scale.multiplyScalar(0.5);
  world.add(gapLabel);

  // pilar selisih
  const gapBar = new THREE.Mesh(
    new THREE.BoxGeometry(0.05, 1, 0.05),
    new THREE.MeshBasicMaterial({ color: 0xff5470, transparent: true, opacity: 0.34 })
  );
  world.add(gapBar);

  attachControls(stage, {
    zoom: false, autoRotate: true, autoRotateSpeed: 0.5,
    target: [0, 0.55, 0], minPolar: 0.45, maxPolar: Math.PI / 2 + 0.05
  });
  stage.camera.position.set(0.6, 1.9, 6.8);
  stage.controls.update();

  // pada layar sempit, mundurkan kamera agar penggaris & label tetap masuk
  stage.onResize = (w) => {
    const c = stage.controls;
    if (!c) return;
    const dist = w < 620 ? 8.6 : w < 900 ? 7.6 : 6.8;
    const off = stage.camera.position.clone().sub(c.target);
    off.setLength(dist);
    stage.camera.position.copy(c.target).add(off);
    c.update();
  };

  /* ── state & animasi ── */
  const state = { hN: 87.1, hS: 81.0, cN: 87.1, cS: 81.0 };
  const lastText = { n: '', s: '', g: '' };
  let labelClock = 0;

  function apply(months) {
    // rata-rata laki-laki & perempuan agar netral
    const rM = D.whoRef('M', months), rF = D.whoRef('F', months);
    const median = (rM.median + rF.median) / 2;
    const sd = (rM.sd + rF.sd) / 2;
    state.hN = median;
    state.hS = median - 2 * sd;
  }

  window.addEventListener('age-change', (e) => apply(e.detail.months));

  // ambil nilai awal dari slider bila sudah ada
  const initial = document.getElementById('ageRange');
  apply(initial ? +initial.value : 24);
  state.cN = state.hN; state.cS = state.hS;

  stage.onFrame = (dt, t) => {
    state.cN += (state.hN - state.cN) * Math.min(1, dt * 6);
    state.cS += (state.hS - state.cS) * Math.min(1, dt * 6);

    const yN = state.cN * SCALE, yS = state.cS * SCALE;
    normal.scale.setScalar(yN);
    stunt.scale.setScalar(yS);

    // gerak bernapas halus
    if (!reduceMotion) {
      normal.position.y = Math.sin(t * 1.1) * 0.012;
      stunt.position.y = Math.sin(t * 1.1 + 1.2) * 0.012;
      normal.rotation.y = Math.sin(t * 0.42) * 0.22;
      stunt.rotation.y = Math.sin(t * 0.42 + 0.7) * 0.22;
    }

    labNormal.position.set(-1.15, yN + 0.30, 0.35);
    labStunt.position.set(1.15, yS + 0.30, 0.35);

    gapLine.position.y = yS;
    gapBar.position.set(1.15, (yS + yN) / 2, 0.35);
    gapBar.scale.y = Math.max(0.001, (yN - yS));
    gapLabel.position.set(1.15, (yS + yN) / 2, 0.62);

    // teks label hanya digambar ulang saat isinya benar-benar berubah,
    // dan paling sering 12×/detik — bukan tiap frame.
    labelClock += dt;
    if (labelClock > 1 / 12) {
      labelClock = 0;
      const tN = `${state.cN.toFixed(1)} cm`;
      const tS = `${state.cS.toFixed(1)} cm`;
      const tG = `selisih ${(state.cN - state.cS).toFixed(1)} cm`;
      if (tN !== lastText.n) {
        lastText.n = tN;
        setLabel(labNormal, tN, { size: 40, color: '#2ee6c5' });
        labNormal.scale.multiplyScalar(0.62);
      }
      if (tS !== lastText.s) {
        lastText.s = tS;
        setLabel(labStunt, tS, { size: 40, color: '#ff8a3d' });
        labStunt.scale.multiplyScalar(0.62);
      }
      if (tG !== lastText.g) {
        lastText.g = tG;
        setLabel(gapLabel, tG, { size: 34, color: '#ff5470' });
        gapLabel.scale.multiplyScalar(0.5);
      }
    }
  };

  return stage;
}

/* ═══════════════════════════════════════════════════════
   3. MAP — diagram batang prevalensi 3D
   ═══════════════════════════════════════════════════════ */
function initMap() {
  const canvas = document.getElementById('mapCanvas');
  if (!canvas) return;
  const stage = createStage(canvas, { camera: [0, 12, 18], fov: 46 });
  const { scene, camera } = stage;

  scene.add(new THREE.AmbientLight(0xa8c0ff, 0.75));
  const d1 = new THREE.DirectionalLight(0xffffff, 1.2); d1.position.set(6, 14, 8); scene.add(d1);
  const d2 = new THREE.DirectionalLight(0x8b6cff, 0.5); d2.position.set(-8, 6, -8); scene.add(d2);

  const world = new THREE.Group();
  scene.add(world);

  const provs = D.PROVINCES.slice().sort((a, b) => b.v - a.v);
  const COLS = 8;
  const ROWS = Math.ceil(provs.length / COLS);
  const GAP = 1.5;
  const offX = ((COLS - 1) * GAP) / 2;
  const offZ = ((ROWS - 1) * GAP) / 2;

  // alas
  const base = new THREE.Mesh(
    new THREE.BoxGeometry(COLS * GAP + 1.4, 0.18, ROWS * GAP + 1.4),
    new THREE.MeshStandardMaterial({ color: 0x0a1322, roughness: 0.92, metalness: 0.04 })
  );
  base.position.y = -0.09;
  world.add(base);

  const grid = new THREE.GridHelper(Math.max(COLS, ROWS) * GAP + 1.4, Math.max(COLS, ROWS), 0x1b2f4d, 0x152640);
  grid.material.transparent = true; grid.material.opacity = 0.4;
  grid.position.y = 0.005;
  world.add(grid);

  const cLow = new THREE.Color(0x2ee6c5);
  const cMid = new THREE.Color(0xffb648);
  const cHigh = new THREE.Color(0xff5470);
  const colorFor = (v) => {
    const t = Math.min(1, Math.max(0, (v - 5) / 35));
    return t < 0.5 ? cLow.clone().lerp(cMid, t * 2) : cMid.clone().lerp(cHigh, (t - 0.5) * 2);
  };

  const HSCALE = 0.16;   // 1% → 0.16 satuan
  const barGeo = new THREE.BoxGeometry(0.9, 1, 0.9);
  const bars = [];

  provs.forEach((p, i) => {
    const col = i % COLS, row = (i / COLS) | 0;
    const c = colorFor(p.v);
    const mat = new THREE.MeshStandardMaterial({
      color: c, emissive: c.clone().multiplyScalar(0.22),
      roughness: 0.32, metalness: 0.28
    });
    const m = new THREE.Mesh(barGeo, mat);
    m.position.set(col * GAP - offX, 0, row * GAP - offZ);
    m.userData = { prov: p, target: p.v * HSCALE, current: 0, base: c.clone(), i };
    m.scale.y = 0.001;
    world.add(m);
    bars.push(m);

    // pijar dasar
    const halo = new THREE.Mesh(
      new THREE.CircleGeometry(0.62, 24),
      new THREE.MeshBasicMaterial({ color: c, transparent: true, opacity: 0.16 })
    );
    halo.rotation.x = -Math.PI / 2;
    halo.position.set(m.position.x, 0.012, m.position.z);
    world.add(halo);
  });

  // bidang acuan target nasional 14%
  const targetY = D.TARGET * HSCALE;
  const plane = new THREE.Mesh(
    new THREE.PlaneGeometry(COLS * GAP + 1.4, ROWS * GAP + 1.4),
    new THREE.MeshBasicMaterial({ color: 0x2ee6c5, transparent: true, opacity: 0.07, side: THREE.DoubleSide })
  );
  plane.rotation.x = -Math.PI / 2;
  plane.position.y = targetY;
  world.add(plane);

  const targetLabel = makeLabel('Target nasional 14%', { size: 34, color: '#2ee6c5' });
  targetLabel.scale.multiplyScalar(0.75);
  targetLabel.position.set(-offX - 1.4, targetY + 0.35, offZ + 1.2);
  world.add(targetLabel);

  attachControls(stage, {
    zoom: true, autoRotate: true, autoRotateSpeed: 0.42,
    target: [0, 1.4, 0], min: 8, max: 34, minPolar: 0.18, maxPolar: Math.PI / 2 - 0.04
  });
  camera.position.set(9, 11, 15);

  /* ── hover ── */
  const ray = new THREE.Raycaster();
  const ptr = new THREE.Vector2();
  const tip = document.getElementById('mapTip');
  const tipName = document.getElementById('tipName');
  const tipVal = document.getElementById('tipVal');
  const tipStatus = document.getElementById('tipStatus');
  let hovered = null;

  function highlight(mesh) {
    if (hovered === mesh) return;
    if (hovered) {
      hovered.material.emissive.copy(hovered.userData.base).multiplyScalar(0.22);
      hovered.userData.hover = false;
    }
    hovered = mesh;
    if (hovered) {
      hovered.material.emissive.copy(hovered.userData.base).multiplyScalar(0.9);
      hovered.userData.hover = true;
    }
  }

  canvas.addEventListener('pointermove', (e) => {
    const r = canvas.getBoundingClientRect();
    ptr.x = ((e.clientX - r.left) / r.width) * 2 - 1;
    ptr.y = -((e.clientY - r.top) / r.height) * 2 + 1;
    ray.setFromCamera(ptr, camera);
    const hit = ray.intersectObjects(bars, false)[0];
    if (hit) {
      highlight(hit.object);
      const p = hit.object.userData.prov;
      tipName.textContent = p.n;
      tipVal.textContent = p.v.toFixed(1) + '%';
      tipStatus.textContent = D.provStatus(p.v);
      tip.hidden = false;
      tip.style.left = (e.clientX - r.left) + 'px';
      tip.style.top = (e.clientY - r.top) + 'px';
      canvas.style.cursor = 'pointer';
    } else {
      highlight(null);
      tip.hidden = true;
      canvas.style.cursor = 'grab';
    }
  });
  canvas.addEventListener('pointerleave', () => { highlight(null); tip.hidden = true; });

  // fokuskan dari daftar peringkat
  window.addEventListener('focus-province', (e) => {
    const m = bars.find(b => b.userData.prov.n === e.detail.name);
    if (!m) return;
    highlight(m);
    setTimeout(() => { if (hovered === m) highlight(null); }, 2200);
  });

  /* ── animasi tumbuh saat masuk layar ── */
  let grown = false;
  new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) grown = true;
  }, { threshold: 0.2 }).observe(stage.host);

  stage.onFrame = (dt, t) => {
    bars.forEach((b, i) => {
      const u = b.userData;
      const goal = grown ? u.target : 0.001;
      const delay = Math.min(1, Math.max(0, (t * 1.6) - i * 0.03));
      const want = goal * (grown ? delay : 1);
      u.current += (want - u.current) * Math.min(1, dt * 5);
      const h = Math.max(0.001, u.current) + (u.hover ? 0.18 : 0);
      b.scale.y = h;
      b.position.y = h / 2;
    });
    plane.material.opacity = 0.06 + Math.sin(t * 1.3) * 0.02;
  };

  return stage;
}

/* ═══════════════════════════════════════════════════════
   4. NUTRIENTS — model molekul bola-batang
   ═══════════════════════════════════════════════════════ */
function initNutrients() {
  const canvas = document.getElementById('nutriCanvas');
  if (!canvas) return;
  const stage = createStage(canvas, { camera: [0, 0, 13], fov: 42 });
  const { scene } = stage;

  scene.add(new THREE.AmbientLight(0xa8c0ff, 0.6));
  const d1 = new THREE.DirectionalLight(0xffffff, 1.5); d1.position.set(5, 6, 8); scene.add(d1);
  const d2 = new THREE.DirectionalLight(0x4a8cff, 0.7); d2.position.set(-6, -3, 4); scene.add(d2);
  const p1 = new THREE.PointLight(0x2ee6c5, 90, 40); p1.position.set(-6, 4, 6); scene.add(p1);

  const world = new THREE.Group();
  scene.add(world);

  // aura latar
  const aura = new THREE.Mesh(
    new THREE.SphereGeometry(9, 32, 24),
    new THREE.MeshBasicMaterial({ color: 0x2ee6c5, transparent: true, opacity: 0.035, side: THREE.BackSide })
  );
  scene.add(aura);

  // debu latar
  const dustN = 240, dpos = new Float32Array(dustN * 3);
  for (let i = 0; i < dustN; i++) {
    dpos[i * 3] = (Math.random() - 0.5) * 26;
    dpos[i * 3 + 1] = (Math.random() - 0.5) * 18;
    dpos[i * 3 + 2] = (Math.random() - 0.5) * 18 - 4;
  }
  const dGeo = new THREE.BufferGeometry();
  dGeo.setAttribute('position', new THREE.BufferAttribute(dpos, 3));
  const dust = new THREE.Points(dGeo, new THREE.PointsMaterial({
    color: 0x6f93c9, size: 0.07, transparent: true, opacity: 0.5, depthWrite: false
  }));
  scene.add(dust);

  const molGroup = new THREE.Group();
  world.add(molGroup);

  const sphereGeo = new THREE.IcosahedronGeometry(1, 4);
  const bondGeo = new THREE.CylinderGeometry(1, 1, 1, 12);
  const matCache = new Map();
  function elMaterial(el) {
    if (!matCache.has(el)) {
      const spec = D.EL[el] || D.EL.C;
      matCache.set(el, new THREE.MeshStandardMaterial({
        color: spec.c, roughness: 0.24, metalness: 0.45,
        emissive: new THREE.Color(spec.c).multiplyScalar(0.16)
      }));
    }
    return matCache.get(el);
  }
  const bondMat = new THREE.MeshStandardMaterial({
    color: 0x8fa6c8, roughness: 0.5, metalness: 0.3,
    emissive: 0x1a2a44, transparent: true, opacity: 0.92
  });

  const up = new THREE.Vector3(0, 1, 0);
  const va = new THREE.Vector3(), vb = new THREE.Vector3(), dir = new THREE.Vector3();

  function clearMolecule() {
    while (molGroup.children.length) {
      const c = molGroup.children.pop();
      molGroup.remove(c);
    }
  }

  let animItems = [];

  let elapsed = 0;

  function buildMolecule(mol) {
    clearMolecule();
    animItems = [];
    elapsed = 0;   // ulang animasi masuk untuk molekul baru

    const atoms = mol.atoms, bonds = mol.bonds || [];
    // pusatkan
    const center = new THREE.Vector3();
    atoms.forEach(a => center.add(new THREE.Vector3(...a.p)));
    center.divideScalar(atoms.length || 1);

    const nodes = atoms.map((a, i) => {
      const spec = D.EL[a.el] || D.EL.C;
      const m = new THREE.Mesh(sphereGeo, elMaterial(a.el));
      m.position.set(a.p[0] - center.x, a.p[1] - center.y, a.p[2] - center.z);
      m.userData.r = spec.r;
      m.scale.setScalar(0.001);
      molGroup.add(m);
      animItems.push({ mesh: m, target: spec.r, delay: i * 0.018 });
      return m;
    });

    bonds.forEach((b, i) => {
      const A = nodes[b[0]], B = nodes[b[1]];
      if (!A || !B) return;
      va.copy(A.position); vb.copy(B.position);
      dir.subVectors(vb, va);
      const len = dir.length();
      if (len < 1e-4) return;   // arah tak terdefinisi → lewati
      const m = new THREE.Mesh(bondGeo, bondMat);
      m.position.copy(va).addScaledVector(dir, 0.5);
      m.quaternion.setFromUnitVectors(up, dir.clone().normalize());
      m.scale.set(0.001, len, 0.001);
      molGroup.add(m);
      animItems.push({ mesh: m, target: 0.085, delay: 0.15 + i * 0.012, isBond: true, len });
    });

    // sesuaikan jarak kamera dengan ukuran molekul
    let maxR = 1;
    nodes.forEach(n => { maxR = Math.max(maxR, n.position.length()); });
    stage.camera.position.z = THREE.MathUtils.clamp(maxR * 2.35 + 3.4, 9, 20);
  }

  function setNutrient(id) {
    const n = D.NUTRIENTS.find(x => x.id === id) || D.NUTRIENTS[0];
    buildMolecule(n.mol);
    aura.material.color.set(n.color);
    p1.color.set(n.color);
  }

  window.addEventListener('nutrient-change', (e) => setNutrient(e.detail.id));
  setNutrient(D.NUTRIENTS[0].id);

  attachControls(stage, {
    zoom: false, autoRotate: true, autoRotateSpeed: 1.1,
    target: [0, 0, 0], minPolar: 0.15, maxPolar: Math.PI - 0.15
  });

  stage.onFrame = (dt, t) => {
    elapsed += dt;
    animItems.forEach(it => {
      const k = Math.min(1, Math.max(0, (elapsed - it.delay) * 4));
      const e = 1 - Math.pow(1 - k, 3);
      if (it.isBond) {
        it.mesh.scale.set(it.target * e, it.len, it.target * e);
      } else {
        it.mesh.scale.setScalar(it.target * e);
      }
    });
    molGroup.rotation.x = Math.sin(t * 0.32) * 0.18;
    dust.rotation.y = t * 0.02;
    aura.rotation.y = -t * 0.03;
  };

  return stage;
}

/* ═══════════════════════════════════════════════════════
   BOOT
   ═══════════════════════════════════════════════════════ */
function boot() {
  if (!hasWebGL()) {
    document.querySelectorAll('canvas').forEach(c => {
      if (c.id === 'growthChart') return;
      const host = c.parentElement;
      if (!host) return;
      c.remove();
      const msg = document.createElement('div');
      msg.className = 'stage-badge';
      msg.style.cssText = 'position:absolute;inset:auto 1rem 1rem 1rem;top:50%;transform:translateY(-50%);text-align:center;line-height:1.6';
      msg.textContent = 'Peramban Anda tidak mendukung WebGL, sehingga visualisasi 3D tidak dapat ditampilkan. Seluruh konten dan kalkulator tetap dapat digunakan.';
      host.appendChild(msg);
    });
    window.dispatchEvent(new CustomEvent('scenes-ready'));
    return;
  }

  const stages = {};
  try { stages.hero = initHero(); } catch (e) { console.warn('[hero]', e); }
  try { stages.growth = initGrowth(); } catch (e) { console.warn('[growth]', e); }
  try { stages.map = initMap(); } catch (e) { console.warn('[map]', e); }
  try { stages.nutri = initNutrients(); } catch (e) { console.warn('[nutri]', e); }

  window.SCENES = stages;
  window.dispatchEvent(new CustomEvent('scenes-ready'));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
